import { useState, type FormEvent } from 'react'
import { MapPin, Phone, Clock, Send } from 'lucide-react'
import WhatsAppIcon from '@/components/icons/WhatsAppIcon'
import SocialLinks from '@/components/SocialLinks'
import { HERO_IMAGE_URL } from '@/data/media'
import { useContent, useLiens } from '@/hooks/useContent'
import { mapsEmbedUrl } from '@/helpers/liens'

export default function ContactPage() {
  const [nom, setNom] = useState('')
  const [message, setMessage] = useState('')
  const { contact } = useContent()
  const liens = useLiens()

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    const texte = `Bonjour, je suis ${nom.trim() || 'un visiteur du site'}.\n\n${message.trim()}`
    window.open(`${liens.whatsapp}?text=${encodeURIComponent(texte)}`, '_blank', 'noopener,noreferrer')
  }

  return (
    <div>
      <section className="relative text-white px-6 py-16 md:py-20 text-center overflow-hidden">
        {/* Photo de l'assemblée — image Unsplash de PRÉVISUALISATION (voir data/media.ts). */}
        <img src={HERO_IMAGE_URL} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-linear-to-b from-cmp-navy-dark/90 via-cmp-navy/75 to-cmp-navy-dark/95" />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 60% 50% at 20% 20%, rgba(201,147,47,0.2) 0%, transparent 100%)',
          }}
        />

        <div className="relative z-10 max-w-xl mx-auto">
          <p className="text-cmp-gold-light text-xs font-bold uppercase tracking-[0.2em] mt-6 mb-3">Contact</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight tracking-tight text-balance">
            Parlons-nous
          </h1>
          <p className="text-white/80 text-base md:text-lg leading-relaxed mt-4 max-w-md mx-auto">
            Une question, besoin d'un renseignement ? L'équipe vous répond avec plaisir.
          </p>

          {/* Bouton WhatsApp visible sans avoir à faire défiler la page */}
          <a
            href={liens.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center justify-center gap-2 bg-green-600 text-white font-bold rounded-full py-3.5 px-7 text-sm shadow-md shadow-green-600/25 hover:bg-green-700 transition-colors active:scale-95"
          >
            <WhatsAppIcon size={18} />
            Nous écrire sur WhatsApp
          </a>
        </div>
      </section>

      <section className="px-4 py-14 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto grid gap-4 md:gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-cmp-cream rounded-2xl rounded-tl-3xl p-6">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
              <MapPin size={17} className="text-cmp-blue" />
            </div>
            <p className="font-bold text-cmp-navy text-sm mt-4">Adresse</p>
            <p className="text-gray-500 text-sm mt-1 leading-relaxed">{contact.adresse}</p>
            {contact.repere && <p className="text-gray-400 text-xs mt-1">{contact.repere}</p>}
            <a
              href={liens.maps}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-cmp-blue text-xs font-semibold mt-3 hover:gap-1.5 transition-all"
            >
              Ouvrir l'itinéraire
            </a>
          </div>

          <div className="bg-cmp-cream rounded-2xl rounded-tl-3xl p-6">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
              <Phone size={17} className="text-cmp-blue" />
            </div>
            <p className="font-bold text-cmp-navy text-sm mt-4">Téléphone</p>
            <a href={liens.tel} className="text-gray-600 text-sm mt-1 leading-relaxed block hover:text-cmp-blue transition-colors">
              {contact.telephone}
            </a>
            {contact.email && (
              <a href={`mailto:${contact.email}`} className="text-gray-600 text-sm leading-relaxed block hover:text-cmp-blue transition-colors break-all">
                {contact.email}
              </a>
            )}
            <p className="text-gray-400 text-xs mt-1">Appelez-nous directement</p>
          </div>

          <div className="bg-cmp-cream rounded-2xl rounded-tl-3xl p-6">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
              <Clock size={17} className="text-cmp-blue" />
            </div>
            <p className="font-bold text-cmp-navy text-sm mt-4">Secrétariat</p>
            <p className="text-gray-500 text-sm mt-1 leading-relaxed">{contact.secretariatHoraires}</p>
            <p className="text-gray-400 text-xs mt-1">{contact.secretariatJours}</p>
          </div>

          <div className="bg-cmp-cream rounded-2xl rounded-tl-3xl p-6">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
              <WhatsAppIcon size={17} />
            </div>
            <p className="font-bold text-cmp-navy text-sm mt-4">Suivez-nous</p>
            <p className="text-gray-500 text-sm mt-1 leading-relaxed">Nos actualités et prédications</p>
            <SocialLinks
              className="mt-3 flex-wrap"
              iconSize={14}
              buttonClassName="w-8 h-8 bg-white text-cmp-navy shadow-sm hover:bg-cmp-sky-light"
            />
          </div>
        </div>
      </section>

      {/* Le formulaire n'envoie pas d'e-mail : il ouvre WhatsApp avec le message déjà rédigé */}
      <section className="px-4 pb-16 md:pb-20">
        <div className="max-w-lg md:max-w-4xl mx-auto md:grid md:grid-cols-2 gap-6 md:items-stretch">
          {/* Repère visuel — remplacer par une carte interactive (Google Maps embed) une fois l'adresse confirmée */}
          <div
            className="mb-5 lg:mb-0 relative rounded-3xl rounded-tl-4xl overflow-hidden ring-1 ring-cmp-navy/5"
            style={{
              backgroundImage:
                'linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)',
              backgroundSize: '28px 28px',
              backgroundColor: '#f8fafc',
            }}
            aria-hidden="true"
          >
            <div className="lg:absolute inset-0 flex items-center justify-center">
              <iframe
                title="Carte de l'église"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                src={mapsEmbedUrl(contact.lat, contact.lng)}
              />
            </div>
          </div>

          <div className="bg-cmp-cream rounded-3xl rounded-tl-4xl p-8">
          <h2 className="text-xl md:text-2xl font-bold text-cmp-navy text-center">Écrivez-nous</h2>
          <p className="text-gray-500 text-sm leading-relaxed text-center mt-2">
            Votre message s'ouvrira directement dans WhatsApp — rien ne se perd, et l'église vous
            répond avec l'outil qu'elle utilise déjà tous les jours.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="contact-nom" className="block text-xs font-bold text-cmp-navy uppercase tracking-wide mb-1.5">
                Votre nom
              </label>
              <input
                id="contact-nom"
                type="text"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                placeholder="Votre nom"
                className="w-full rounded-xl border border-cmp-navy/15 bg-white px-4 py-3 text-sm text-cmp-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-cmp-blue/40"
              />
            </div>

            <div>
              <label htmlFor="contact-message" className="block text-xs font-bold text-cmp-navy uppercase tracking-wide mb-1.5">
                Votre message
              </label>
              <textarea
                id="contact-message"
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Écrivez votre message ici…"
                rows={4}
                className="w-full rounded-xl border border-cmp-navy/15 bg-white px-4 py-3 text-sm text-cmp-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-cmp-blue/40 resize-none"
              />
            </div>

            <button
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-green-600 text-white font-bold rounded-full py-3.5 text-sm shadow-md shadow-green-600/25 hover:bg-green-700 transition-colors active:scale-95"
            >
              <Send size={15} />
              Envoyer sur WhatsApp
            </button>
          </form>
          </div>
        </div>
      </section>
    </div>
  )
}
